import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TagoLanding = () => {
  const [formData, setFormData] = useState({
    country_code: '+973',
    phone: '',
    email: '',
    name: '',
    city: '',
    user_type: '',
    source: '',
    device: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(246);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { type: 'bot', text: '👋 Hi! I\'m TAGO\'s AI assistant. Ask me anything about how TAGO works, the launch date, pricing, or how to sign up.<br/><br/>What would you like to know?' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  
  const waAnimationStarted = useRef(false);
  const [waMessageIndex, setWaMessageIndex] = useState(0);
  const [showTyping, setShowTyping] = useState(false);

  // Fetch waitlist count on mount
  useEffect(() => {
    fetchWaitlistCount();
  }, []);

  const fetchWaitlistCount = async () => {
    try {
      const response = await axios.get(`${API}/waitlist/count`);
      setWaitlistCount(response.data.count || 246);
    } catch (error) {
      console.error('Error fetching waitlist count:', error);
    }
  };

  // Scroll reveal effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-6');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // WhatsApp animation
  useEffect(() => {
    const waSection = document.getElementById('wa-section');
    if (!waSection || waAnimationStarted.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !waAnimationStarted.current) {
          waAnimationStarted.current = true;
          animateWAMessages();
        }
      },
      { threshold: 0.4 }
    );

    observer.observe(waSection);
    return () => observer.disconnect();
  }, []);

  const animateWAMessages = () => {
    const sequence = [
      { index: 0, delay: 600 },
      { typing: true, delay: 1800 },
      { index: 1, delay: 1300 },
      { index: 2, delay: 7000 },
      { reset: true, delay: 1000 }
    ];

    let step = 0;
    const runStep = () => {
      if (step >= sequence.length) return;
      
      const current = sequence[step];
      
      if (current.typing) {
        setShowTyping(true);
      } else if (current.reset) {
        setWaMessageIndex(0);
        setShowTyping(false);
        step = 0;
        setTimeout(runStep, current.delay);
        return;
      } else {
        setShowTyping(false);
        setWaMessageIndex(current.index + 1);
      }
      
      step++;
      setTimeout(runStep, current.delay);
    };

    setTimeout(runStep, 600);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.phone || !formData.email) {
      alert('Please fill in WhatsApp number and email');
      return;
    }

    try {
      await axios.post(`${API}/waitlist`, formData);
      setIsSubmitted(true);
      fetchWaitlistCount();
      launchConfetti();
    } catch (error) {
      console.error('Error submitting waitlist:', error);
      alert('Something went wrong. Please try again.');
    }
  };

  const launchConfetti = () => {
    const colors = ['#00C4B4', '#FF6B35', '#333', '#FFD700', '#22C55E'];
    for (let i = 0; i < 72; i++) {
      const piece = document.createElement('div');
      piece.className = 'confetti-piece';
      piece.style.cssText = `
        position: fixed;
        top: -10px;
        left: ${Math.random() * 100}vw;
        width: ${4 + Math.random() * 8}px;
        height: ${4 + Math.random() * 8}px;
        background: ${colors[Math.floor(Math.random() * colors.length)]};
        border-radius: ${Math.random() > 0.5 ? '50%' : '3px'};
        pointer-events: none;
        z-index: 9999;
        animation: confetti-fall ${1.5 + Math.random() * 2}s ${Math.random() * 0.8}s linear both;
      `;
      document.body.appendChild(piece);
      setTimeout(() => piece.remove(), 3800);
    }
  };

  const chatReplies = {
    'how does it work': 'TAGO is simple: 1️⃣ A QR tag goes on your vehicle. 2️⃣ Anyone who needs to reach you scans it. 3️⃣ They select the reason. 4️⃣ You get a private WhatsApp instantly — no app, no number exposed!',
    'when does it launch': '🚀 TAGO launches in <strong>May 2026</strong> in Bahrain. Join the founding waitlist now to get first access and exclusive pricing!',
    'is it free': 'The TAGO tag has a one-time cost of BHD 4–6. Basic vehicle alerts are free after that. Business and fleet plans have monthly subscriptions. Founding members get special rates! 🎉',
    'business pricing': '💼 Business plans start from BHD 50/month for buildings and facilities, BHD 2–5 per rider/month for delivery fleets. Contact us for a custom quote!',
  };

  const sendChatMessage = (message) => {
    if (!message.trim()) return;

    setChatMessages([...chatMessages, { type: 'user', text: message }]);
    setShowQuickReplies(false);
    setChatInput('');

    const key = Object.keys(chatReplies).find(k => message.toLowerCase().includes(k));
    const reply = key ? chatReplies[key] : 'Great question! TAGO is Bahrain\'s first intelligent vehicle contact platform — launching May 2026. Join our waitlist above or ask me anything specific! 🚗';

    setTimeout(() => {
      setChatMessages(prev => [...prev, { type: 'bot', text: reply }]);
    }, 550);
  };

  const sendQuickReply = (text) => {
    sendChatMessage(text);
  };

  return (
    <div className="min-h-screen bg-[#040F1E] text-white font-sans overflow-x-hidden relative">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800;900&family=DM+Sans:wght@400;500;600;700;800&family=Cairo:wght@400;600;700&display=swap');
        
        body::before {
          content: '';
          position: fixed;
          inset: 0;
          z-index: 0;
          background-image: linear-gradient(rgba(0,196,180,.03) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(0,196,180,.03) 1px, transparent 1px);
          background-size: 52px 52px;
          pointer-events: none;
        }
        
        @keyframes confetti-fall {
          from { opacity: 1; transform: translateY(0) rotate(0deg); }
          to { opacity: 0; transform: translateY(100vh) rotate(720deg); }
        }
        
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.15; }
        }
        
        @keyframes typing-bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }
        
        @keyframes ring-pulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.12; transform: scale(1.04); }
        }
        
        @keyframes drift {
          from { transform: translate(0,0) scale(1); }
          to { transform: translate(40px,30px) scale(1.08); }
        }
        
        @keyframes rotate-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .font-syne { font-family: 'Syne', sans-serif; }
        .font-dm { font-family: 'DM Sans', sans-serif; }
        .font-cairo { font-family: 'Cairo', sans-serif; }
        
        .reveal {
          opacity: 1;
          transform: translateY(0);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        
        .blink-dot {
          animation: blink 1.8s ease-in-out infinite;
        }
        
        .typing-dot {
          animation: typing-bounce 1.2s ease-in-out infinite;
        }
        
        .ring-pulse {
          animation: ring-pulse 2.5s ease-in-out infinite;
        }
        
        .orb {
          position: fixed;
          border-radius: 50%;
          filter: blur(130px);
          pointer-events: none;
          z-index: 0;
        }
        
        .orb-1 {
          width: min(700px, 90vw);
          height: min(700px, 90vw);
          background: radial-gradient(circle, rgba(0,196,180,.1) 0%, transparent 70%);
          top: -200px;
          right: -150px;
          animation: drift 20s ease-in-out infinite alternate;
        }
        
        .orb-2 {
          width: min(500px, 70vw);
          height: min(500px, 70vw);
          background: radial-gradient(circle, rgba(255,107,53,.07) 0%, transparent 70%);
          bottom: -100px;
          left: -100px;
          animation: drift 25s ease-in-out infinite alternate-reverse;
        }
      `}</style>
      
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>

      {/* Navigation */}
      <nav className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between flex-wrap gap-4">
        <a href="#" className="font-syne text-4xl sm:text-5xl font-extrabold text-[#00C4B4] tracking-tight" style={{letterSpacing: '-1.5px'}}>
          TAGO
        </a>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-2 bg-[rgba(37,211,102,.09)] border border-[rgba(37,211,102,.2)] text-[#25D366] text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
            📱 AI-Powered Messaging
          </span>
          <span className="inline-flex items-center bg-[rgba(0,196,180,.08)] border border-[rgba(0,196,180,.14)] text-[#00C4B4] text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap tracking-wide">
            🇧🇭 Bahrain · May 2026
          </span>
          <a
            href="/login"
            data-testid="nav-login-button"
            className="hidden items-center bg-[#00C4B4] hover:bg-[#00DDD0] text-[#040F1E] text-sm font-bold px-4 py-1.5 rounded-full whitespace-nowrap transition-all hover:shadow-lg hover:shadow-[#00C4B4]/30"
          >
            Login / Register
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 text-center">
        <div className="inline-flex items-center gap-2 bg-[rgba(0,196,180,.07)] border border-[rgba(0,196,180,.14)] rounded-full px-5 py-2 text-xs text-[#00C4B4] font-bold tracking-widest uppercase mb-8">
          <span className="w-2 h-2 bg-[#00C4B4] rounded-full blink-dot"></span>
          Powered by TAGO · Zero App Download
        </div>
        
        <h1 className="font-syne text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-extrabold leading-none tracking-tight mb-4">
          <span className="text-[#00C4B4]">Tag</span> & <span className="text-[#FF6B35]">Reach.</span>
        </h1>
        
        <p className="font-syne text-2xl sm:text-3xl md:text-4xl font-semibold text-[rgba(255,255,255,.7)] mb-6">
          Instant AI-powered dispatch to the right person, every time.
        </p>
        
        <p className="font-cairo text-lg sm:text-xl text-[rgba(255,255,255,.5)] mb-6 font-semibold" dir="rtl">
          ملصق واحد. وصول فوري عبر الذكاء الاصطناعي.
        </p>
        
        <p className="text-lg sm:text-xl text-[rgba(255,255,255,.7)] leading-relaxed max-w-2xl mx-auto mb-10 font-dm font-medium">
          TAGO transforms your vehicle's QR tag into a real-time intelligent dispatch system — instantly connecting the right people via secure messaging the moment something happens to your vehicle. No app. No number shared. 30 seconds.
        </p>

        {/* Flow Strip */}
        <div className="flex items-center justify-center gap-3 flex-wrap mb-10">
          <div className="flex items-center gap-2 bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-xl px-4 py-2.5 text-sm font-semibold text-[rgba(255,255,255,.85)]">
            <span className="text-lg">📱</span> Scan TAGO Tag
          </div>
          <span className="text-[#00C4B4] text-lg font-bold">→</span>
          <div className="flex items-center gap-2 bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-xl px-4 py-2.5 text-sm font-semibold text-[rgba(255,255,255,.85)]">
            <span className="text-lg">⚡</span> Select Incident
          </div>
          <span className="text-[#00C4B4] text-lg font-bold">→</span>
          <div className="flex items-center gap-2 bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-xl px-4 py-2.5 text-sm font-semibold text-[rgba(255,255,255,.85)]">
            <span className="text-lg">📡</span> AI Dispatch
          </div>
          <span className="text-[#00C4B4] text-lg font-bold">→</span>
          <div className="flex items-center gap-2 bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-xl px-4 py-2.5 text-sm font-semibold text-[rgba(255,255,255,.85)]">
            <span className="text-lg">✅</span> Resolved in 30 sec
          </div>
        </div>

        {/* Launch Badge */}
        <div className="inline-flex items-center gap-4 bg-gradient-to-r from-teal-50 to-teal-50/50 border border-[rgba(0,196,180,.14)] rounded-2xl px-7 py-4 mb-10 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-500 via-orange-500 to-transparent"></div>
          <div className="font-syne text-4xl sm:text-5xl font-extrabold text-[#00C4B4] tracking-tight leading-none">
            May 2026
          </div>
          <div className="w-px h-12 bg-teal-300"></div>
          <div className="text-left">
            <div className="font-syne text-lg sm:text-xl font-bold text-white leading-tight">🇧🇭 Bahrain Launch</div>
            <div className="text-xs text-[rgba(255,255,255,.5)] uppercase tracking-wider">Founding waitlist open now</div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20 reveal">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[rgba(0,196,180,.14)] border border-[rgba(0,196,180,.14)] rounded-2xl overflow-hidden">
          {[
            { value: '400K+', label: 'Vehicles in Bahrain' },
            { value: '97%', label: 'Messaging app penetration' },
            { value: '8', label: 'Smart dispatch recipients' },
            { value: '30 sec', label: 'Average resolution time' }
          ].map((stat, i) => (
            <div key={i} className="bg-[#0C2040] p-6 sm:p-8 text-center">
              <div className="font-syne text-4xl sm:text-6xl font-extrabold text-[#00C4B4] tracking-tight mb-1">
                {stat.value}
              </div>
              <div className="text-sm sm:text-base text-[rgba(255,255,255,.7)] font-dm font-semibold">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Free QR Tag CTA Section - Hidden for Build 1 */}
      <div className="hidden relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="relative bg-gradient-to-br from-[#0C2040] to-[#081830] border-2 border-[#00C4B4]/30 rounded-3xl p-8 sm:p-12 text-center overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#00C4B4] to-transparent"></div>
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#00C4B4]/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#FF6B35]/5 rounded-full blur-3xl"></div>

          <div className="inline-flex items-center gap-2 bg-[#00C4B4]/10 border border-[#00C4B4]/20 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 bg-[#22C55E] rounded-full animate-pulse"></span>
            <span className="text-[#00C4B4] text-sm font-bold tracking-wide">100% FREE</span>
          </div>

          <h2 className="font-syne text-3xl sm:text-5xl font-extrabold text-white tracking-tight mb-4" style={{letterSpacing: '-1px'}}>
            Create Your Free <span className="text-[#00C4B4]">QR Vehicle Tag</span>
          </h2>
          <p className="text-base sm:text-lg text-[rgba(255,255,255,.6)] max-w-xl mx-auto mb-8 font-dm leading-relaxed">
            Generate a QR code for your car in seconds. Print it, stick it on your dashboard, and let anyone reach you instantly when needed.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <div className="flex items-center gap-3 text-sm text-white/70">
              <span className="w-8 h-8 bg-[#00C4B4]/10 border border-[#00C4B4]/20 rounded-full flex items-center justify-center text-[#00C4B4] font-bold text-xs">1</span>
              <span className="font-dm font-semibold">Register free</span>
            </div>
            <span className="text-[#00C4B4] font-bold hidden sm:block">&rarr;</span>
            <div className="flex items-center gap-3 text-sm text-white/70">
              <span className="w-8 h-8 bg-[#00C4B4]/10 border border-[#00C4B4]/20 rounded-full flex items-center justify-center text-[#00C4B4] font-bold text-xs">2</span>
              <span className="font-dm font-semibold">Add your vehicle & phone</span>
            </div>
            <span className="text-[#00C4B4] font-bold hidden sm:block">&rarr;</span>
            <div className="flex items-center gap-3 text-sm text-white/70">
              <span className="w-8 h-8 bg-[#00C4B4]/10 border border-[#00C4B4]/20 rounded-full flex items-center justify-center text-[#00C4B4] font-bold text-xs">3</span>
              <span className="font-dm font-semibold">Download & print QR</span>
            </div>
          </div>

          <a
            href="/login"
            data-testid="cta-create-qr-button"
            className="inline-flex items-center gap-3 bg-[#00C4B4] hover:bg-[#00DDD0] text-[#040F1E] font-dm font-extrabold text-lg px-10 py-4 rounded-2xl transition-all hover:shadow-xl hover:shadow-[#00C4B4]/30 hover:scale-105"
          >
            Create My Free QR Tag
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>
          </a>

          <p className="text-white/30 text-xs mt-4 font-dm">No credit card needed. Always free.</p>
        </div>
      </div>

      {/* Dispatch Network - Infographic Style */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center reveal">
        <p className="text-sm text-[#00C4B4] font-bold tracking-widest uppercase mb-3">
          AI Dispatch Technology
        </p>
        <h2 className="font-syne text-5xl sm:text-6xl font-extrabold tracking-tight mb-4">
          Who gets the<br /><span className="text-[#00C4B4]">instant alert?</span>
        </h2>
        <p className="text-lg text-[rgba(255,255,255,.7)] max-w-2xl mx-auto leading-relaxed mb-16 font-dm font-medium">
          Based on the incident selected, TAGO's intelligent AI engine routes the right notification to the right people — simultaneously, privately, securely.
        </p>

        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[rgba(255,107,53,.1)] border border-[rgba(255,107,53,.28)] rounded-full px-6 py-3 text-base font-bold text-[#FF6B35] mb-12">
            📱 TAGO Tag Scanned — Incident Reported
          </div>

          {/* Circular Infographic - TAGO AI Engine in Center */}
          <div className="relative w-full max-w-4xl mx-auto" style={{minHeight: '600px'}}>
            
            {/* Center Hub - TAGO AI Engine */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
              <div className="relative w-32 h-32 sm:w-40 sm:h-40">
                {/* Pulsing rings */}
                <div className="absolute inset-0 border-2 border-[rgba(0,196,180,.2)] rounded-full ring-pulse"></div>
                <div className="absolute inset-[-16px] border-2 border-[rgba(0,196,180,.15)] rounded-full ring-pulse" style={{animationDelay: '0.6s'}}></div>
                <div className="absolute inset-[-32px] border-2 border-[rgba(0,196,180,.1)] rounded-full ring-pulse" style={{animationDelay: '1.2s'}}></div>
                
                {/* Core */}
                <div className="relative z-10 w-full h-full bg-[#0C2040] border-4 border-[#00C4B4] rounded-3xl flex flex-col items-center justify-center gap-2 shadow-2xl shadow-teal-500/30">
                  <span className="text-5xl">🤖</span>
                  <span className="font-syne text-xs sm:text-sm font-bold text-[#00C4B4] tracking-wider">TAGO AI</span>
                </div>
              </div>
            </div>

            {/* Recipient Nodes - Arranged in Circle */}
            {[
              { icon: '🚗', label: 'Vehicle\nOwner', color: '#00C4B4', angle: 0 },
              { icon: '🚑', label: 'Ambulance', color: '#F04444', angle: 45 },
              { icon: '🚔', label: 'Traffic\nPolice', color: '#3B82F6', angle: 90 },
              { icon: '👨‍👩‍👧', label: 'Emergency\nContact', color: '#F5A623', angle: 135 },
              { icon: '🔧', label: 'Vehicle\nRecovery', color: '#8B5CF6', angle: 180 },
              { icon: '🏥', label: 'Hospital', color: '#22C55E', angle: 225 },
              { icon: '🛠️', label: 'Car Repair', color: '#FF6B35', angle: 270 },
              { icon: '📍', label: 'Google\nMaps', color: '#F5A623', angle: 315 }
            ].map((node, i) => {
              const radius = 280; // Distance from center
              const angleRad = (node.angle * Math.PI) / 180;
              const x = Math.cos(angleRad) * radius;
              const y = Math.sin(angleRad) * radius;
              
              return (
                <div
                  key={i}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 group cursor-default"
                  style={{
                    left: '50%',
                    top: '50%',
                    marginLeft: `${x}px`,
                    marginTop: `${y}px`
                  }}
                >
                  {/* Connection Line */}
                  <div 
                    className="absolute z-0"
                    style={{
                      width: `${radius - 80}px`,
                      height: '2px',
                      background: `linear-gradient(to right, ${node.color}20, transparent)`,
                      transform: `rotate(${node.angle + 180}deg)`,
                      transformOrigin: 'right center',
                      right: '50%',
                      top: '50%'
                    }}
                  ></div>
                  
                  {/* Node Card */}
                  <div className="relative z-10 flex flex-col items-center gap-2 bg-[#0C2040] border-2 border-[rgba(0,196,180,.2)] rounded-2xl p-4 min-w-[100px] sm:min-w-[120px] hover:border-[rgba(0,196,180,.6)] hover:scale-110 hover:shadow-2xl transition-all duration-300"
                    style={{borderColor: `${node.color}40`}}>
                    
                    {/* WhatsApp badge */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#25D366] rounded-full flex items-center justify-center text-xs border-2 border-[#040F1E]">
                      💬
                    </div>
                    
                    <span className="text-4xl sm:text-5xl">{node.icon}</span>
                    <span className="font-dm text-xs sm:text-sm font-bold text-white uppercase tracking-wide text-center leading-tight whitespace-pre-line">
                      {node.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="inline-flex items-center gap-3 bg-[rgba(37,211,102,.07)] border border-[rgba(37,211,102,.2)] rounded-full px-6 py-3 text-base font-bold text-[#25D366] mt-16">
            💬 All via Secure Messaging · Zero app required · GPS included
          </div>
        </div>
      </section>


      {/* Real Situations - Completely Redesigned Infographic */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 reveal">
        <div className="text-center mb-16">
          <p className="text-sm text-[#00C4B4] font-bold tracking-widest uppercase mb-3">
            Built for Real Moments
          </p>
          <h2 className="font-syne text-5xl sm:text-6xl font-extrabold tracking-tight mb-6">
            Real Situations.<br /><span className="text-[#00C4B4]">Instant Solutions.</span>
          </h2>
          <p className="text-lg text-[rgba(255,255,255,.7)] max-w-3xl mx-auto leading-relaxed font-dm font-medium">
            Every incident triggers immediate, actionable alerts to the right people. See how TAGO resolves real-world problems in seconds.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              emoji: '🅿️',
              title: 'Blocked in the basement',
              tag: 'PARKING',
              color: '#00C4B4',
              colorRgb: '0,196,180',
              steps: ['📱 Scan', '🚗 Owner', '✅ Moved'],
              time: 'avg 30 sec',
              recipients: ['Vehicle Owner', 'Location Pin'],
              action: 'Resolved'
            },
            {
              emoji: '🚨',
              title: 'Accident on the highway',
              tag: 'EMERGENCY',
              color: '#F04444',
              colorRgb: '240,68,68',
              steps: ['📱 Scan', '🚑 Dispatch', '🏥 En Route'],
              time: 'immediate',
              recipients: ['Ambulance', 'Hospital', 'Family', 'GPS'],
              action: 'Dispatched'
            },
            {
              emoji: '🚔',
              title: 'Owner not responding',
              tag: 'ESCALATION',
              color: '#3B82F6',
              colorRgb: '59,130,246',
              steps: ['📱 Scan', '⏰ Wait', '🚔 Police'],
              time: 'auto-escalation',
              recipients: ['Traffic Police', 'Location'],
              action: 'Escalated'
            },
            {
              emoji: '💡',
              title: 'Headlights left on',
              tag: 'MINOR ALERT',
              color: '#F5A623',
              colorRgb: '245,166,35',
              steps: ['📱 Scan', '🚗 Alert', '🔋 Saved'],
              time: '30 sec',
              recipients: ['Vehicle Owner', 'Location'],
              action: 'Battery Saved'
            },
            {
              emoji: '🔧',
              title: 'Breakdown on the road',
              tag: 'ROADSIDE',
              color: '#8B5CF6',
              colorRgb: '139,92,246',
              steps: ['📱 Scan', '🛠️ Service', '🚗 Fixed'],
              time: 'rapid dispatch',
              recipients: ['Repair Service', 'Recovery', 'GPS'],
              action: 'En Route'
            },
            {
              emoji: '🏫',
              title: 'School gate blocked',
              tag: 'B2B',
              color: '#22C55E',
              colorRgb: '34,197,94',
              steps: ['📱 Scan', '👨‍👩‍👧 Parent', '✅ Clear'],
              time: '60 sec',
              recipients: ['Parent', 'School Gate', 'Pin'],
              action: 'Clear'
            }
          ].map((scenario, i) => (
            <div 
              key={i} 
              className="group relative bg-[#0C2040] border-2 rounded-3xl p-6 hover:scale-105 hover:shadow-2xl transition-all duration-300 cursor-default overflow-hidden"
              style={{borderColor: `${scenario.color}40`}}
            >
              {/* Top glow effect */}
              <div 
                className="absolute top-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{background: `linear-gradient(90deg, ${scenario.color}, transparent)`}}
              ></div>
              
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-5xl">{scenario.emoji}</span>
                  <div>
                    <h3 className="font-syne text-lg font-bold text-white leading-tight mb-1">{scenario.title}</h3>
                    <span 
                      className="inline-block text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full"
                      style={{
                        background: `rgba(${scenario.colorRgb},.15)`,
                        color: scenario.color,
                        border: `1px solid rgba(${scenario.colorRgb},.3)`
                      }}
                    >
                      {scenario.tag}
                    </span>
                  </div>
                </div>
              </div>

              {/* Flow Steps */}
              <div className="bg-[rgba(0,0,0,.3)] rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between gap-2">
                  {scenario.steps.map((step, j) => (
                    <React.Fragment key={j}>
                      <div className="flex flex-col items-center gap-1 flex-1">
                        <span className="text-xl">{step.split(' ')[0]}</span>
                        <span className="text-[10px] text-[rgba(255,255,255,.6)] font-dm font-medium text-center">
                          {step.split(' ')[1]}
                        </span>
                      </div>
                      {j < scenario.steps.length - 1 && (
                        <span className="text-xl opacity-40" style={{color: scenario.color}}>→</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Recipients */}
              <div className="mb-4">
                <div className="text-[11px] text-[rgba(255,255,255,.5)] font-dm font-semibold uppercase tracking-wider mb-2">
                  Notified:
                </div>
                <div className="flex flex-wrap gap-2">
                  {scenario.recipients.map((recipient, j) => (
                    <span 
                      key={j}
                      className="text-xs px-3 py-1 rounded-full bg-[rgba(255,255,255,.06)] border border-[rgba(255,255,255,.1)] text-[rgba(255,255,255,.8)] font-dm font-medium"
                    >
                      {recipient}
                    </span>
                  ))}
                </div>
              </div>

              {/* Status & Time */}
              <div className="flex items-center justify-between pt-4 border-t border-[rgba(255,255,255,.1)]">
                <div 
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg font-dm font-bold text-sm"
                  style={{
                    background: `rgba(${scenario.colorRgb},.15)`,
                    color: scenario.color
                  }}
                >
                  <span className="w-2 h-2 rounded-full blink-dot" style={{background: scenario.color}}></span>
                  {scenario.action}
                </div>
                <span className="text-xs text-[rgba(255,255,255,.5)] font-dm font-medium">
                  {scenario.time}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WhatsApp Section */}
      <section id="wa-section" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 reveal">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs text-[#00C4B4] font-bold tracking-widest uppercase mb-3">
              AI-Powered Platform
            </p>
            <h2 className="font-syne text-5xl sm:text-6xl font-extrabold tracking-tight mb-4">
              Secure Messaging.<br /><span className="text-[#00C4B4]">Zero Downloads.</span>
            </h2>
            <p className="text-lg text-[rgba(255,255,255,.7)] leading-relaxed mb-6 font-dm font-semibold">
              TAGO uses proven secure messaging technology — the same platform trusted by millions across the GCC for instant, private communication.
            </p>

            <div className="space-y-3">
              {[
                { icon: '💬', title: 'Structured alert to owner', desc: 'Incident type, location, and one-tap response — all in one message.' },
                { icon: '📡', title: 'Parallel dispatch to all parties', desc: 'Police, ambulance, family, repair — all notified simultaneously.' },
                { icon: '📍', title: 'Google Maps pin in every message', desc: 'Every recipient arrives at the exact right location, instantly.' },
                { icon: '🔒', title: 'End-to-end privacy by design', desc: 'Reporter number is never shared — not even with TAGO.' }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-xl hover:border-[rgba(0,196,180,.4)] hover:translate-x-1 transition-all">
                  <span className="text-2xl flex-shrink-0 mt-0.5">{item.icon}</span>
                  <div>
                    <div className="font-dm font-bold text-base text-white mb-1">{item.title}</div>
                    <div className="text-sm text-[rgba(255,255,255,.7)] leading-relaxed font-dm">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* WhatsApp Phone Mockup */}
          <div className="hidden md:block">
            <div className="w-72 mx-auto bg-[#111] rounded-[42px] p-3.5 border-2 border-[rgba(255,255,255,.07)] shadow-2xl">
              <div className="w-20 h-5 bg-[#111] rounded-b-2xl mx-auto mb-2.5 relative z-10"></div>
              <div className="bg-[#075e54] rounded-[27px] overflow-hidden min-h-[450px]">
                {/* WhatsApp Header */}
                <div className="bg-[#075e54] p-3 flex items-center gap-2 border-b border-white/10">
                  <div className="w-9 h-9 rounded-full bg-[#040F1E]/10 flex items-center justify-center text-sm flex-shrink-0">
                    🚗
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white leading-tight">TAGO Alert</div>
                    <div className="text-[10px] text-white/50">
                      <span className="inline-block w-1.5 h-1.5 bg-teal-400 rounded-full mr-1 blink-dot"></span>
                      Official Vehicle Platform
                    </div>
                  </div>
                </div>

                {/* WhatsApp Body */}
                <div className="bg-[#0d1117] p-3 min-h-[360px] space-y-2">
                  {waMessageIndex >= 1 && (
                    <div className="max-w-[90%] bg-[#1f3340] border-l-4 border-teal-500 rounded-xl rounded-tl-sm p-3 text-white/90 opacity-100 transition-all duration-500">
                      <div className="text-[9px] font-bold text-teal-400 uppercase tracking-wide mb-1">⚠️ TAGO Incident Alert</div>
                      <div className="text-[11px] leading-relaxed mb-2">
                        Your vehicle at <strong>Seef Mall P3</strong> is blocking an exit.<br /><br />
                        📌 Reported: 2:34 PM<br />
                        📍 Location: [tap to view]
                      </div>
                      <div className="bg-[rgba(0,196,180,.08)]0/20 border border-teal-500/40 rounded-lg p-1.5 text-center text-[10px] font-semibold text-teal-400 mb-1">
                        📍 View on Google Maps
                      </div>
                      <div className="bg-[rgba(0,196,180,.08)]0/20 border border-teal-500/40 rounded-lg p-1.5 text-center text-[10px] font-semibold text-teal-400">
                        ✅ I'm on my way
                      </div>
                      <div className="text-[9px] text-white/30 text-right mt-1">2:34 PM ✓✓</div>
                    </div>
                  )}

                  {showTyping && (
                    <div className="flex items-center gap-1 p-2 bg-[#1f3340] rounded-xl rounded-tl-sm w-fit">
                      <div className="w-1.5 h-1.5 rounded-full bg-[rgba(0,196,180,.08)]0 typing-dot"></div>
                      <div className="w-1.5 h-1.5 rounded-full bg-[rgba(0,196,180,.08)]0 typing-dot" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-1.5 h-1.5 rounded-full bg-[rgba(0,196,180,.08)]0 typing-dot" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  )}

                  {waMessageIndex >= 2 && (
                    <div className="max-w-[90%] bg-[#025144] rounded-xl rounded-tr-sm p-3 text-white/90 ml-auto opacity-100 transition-all duration-500">
                      <div className="text-[11px]">Coming now — 3 minutes 🙏</div>
                      <div className="text-[9px] text-white/30 text-right mt-1">2:35 PM ✓✓</div>
                    </div>
                  )}

                  {waMessageIndex >= 3 && (
                    <div className="max-w-[90%] bg-[#1f3340] border-l-4 border-teal-500 rounded-xl rounded-tl-sm p-3 text-white/90 opacity-100 transition-all duration-500">
                      <div className="text-[9px] font-bold text-teal-400 uppercase tracking-wide mb-1">✅ Resolved</div>
                      <div className="text-[11px]">Owner confirmed. Incident closed in <strong>47 seconds</strong>.</div>
                      <div className="text-[9px] text-white/30 text-right mt-1">2:35 PM ✓✓</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist Form */}
      <section className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center reveal">
        <div className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-xl px-5 py-2 text-sm font-semibold text-amber-600 mb-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
          🚀 Launching May 2026 — Join the Founding Waitlist
        </div>

        <h2 className="font-syne text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">🔔 Reserve Your Spot</h2>
        <p className="text-base text-[rgba(255,255,255,.6)] mb-8 leading-relaxed">
          Be among Bahrain's first TAGO-enabled vehicle owners.<br />First access. Founding member pricing.
        </p>

        <div className="bg-[#0C2040] border border-[rgba(0,196,180,.14)] rounded-2xl p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-500 via-orange-500 to-transparent"></div>

          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="text-left">
              <div className="mb-4">
                <label className="block text-[10px] font-semibold text-[rgba(255,255,255,.6)] uppercase tracking-wider mb-2">
                  WhatsApp Number *
                </label>
                <div className="flex gap-2">
                  <select
                    name="country_code"
                    value={formData.country_code}
                    onChange={handleInputChange}
                    className="w-28 bg-[#040F1E] border border-[rgba(0,196,180,.35)] rounded-xl px-2 py-3 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="+973">🇧🇭 +973</option>
                    <option value="+971">🇦🇪 +971</option>
                    <option value="+966">🇸🇦 +966</option>
                    <option value="+974">🇶🇦 +974</option>
                    <option value="+965">🇰🇼 +965</option>
                    <option value="+968">🇴🇲 +968</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+1">🇺🇸 +1</option>
                  </select>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Your WhatsApp number"
                    className="flex-1 bg-[#040F1E] border border-[rgba(0,196,180,.35)] rounded-xl px-4 py-3 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-[10px] font-semibold text-[rgba(255,255,255,.6)] uppercase tracking-wider mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  className="w-full bg-[#040F1E] border border-[rgba(0,196,180,.35)] rounded-xl px-4 py-3 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-[10px] font-semibold text-[rgba(255,255,255,.6)] uppercase tracking-wider mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Ahmad Al Mansoor"
                    className="w-full bg-[#040F1E] border border-[rgba(0,196,180,.35)] rounded-xl px-4 py-3 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-[rgba(255,255,255,.6)] uppercase tracking-wider mb-2">
                    City
                  </label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full bg-[#040F1E] border border-[rgba(0,196,180,.35)] rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2300C4B4' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 14px center',
                      paddingRight: '40px'
                    }}
                  >
                    <option value="">Select city</option>
                    <option value="manama">Manama</option>
                    <option value="riffa">Riffa</option>
                    <option value="juffair">Juffair</option>
                    <option value="seef">Seef District</option>
                    <option value="muharraq">Muharraq</option>
                    <option value="amwaj">Amwaj Islands</option>
                    <option value="isa_town">Isa Town</option>
                    <option value="hamad_town">Hamad Town</option>
                    <option value="dubai">Dubai</option>
                    <option value="abudhabi">Abu Dhabi</option>
                    <option value="riyadh">Riyadh</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-[10px] font-semibold text-[rgba(255,255,255,.6)] uppercase tracking-wider mb-2">
                  I'm joining as
                </label>
                <select
                  name="user_type"
                  value={formData.user_type}
                  onChange={handleInputChange}
                  className="w-full bg-[#040F1E] border border-[rgba(0,196,180,.35)] rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2300C4B4' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 14px center',
                    paddingRight: '40px'
                  }}
                >
                  <option value="">Select your profile</option>
                  <option value="car_owner">🚗 Vehicle Owner</option>
                  <option value="fleet_manager">🚌 Fleet Manager</option>
                  <option value="mall_facility">🏢 Mall / Building Facility</option>
                  <option value="school_admin">🏫 School Administrator</option>
                  <option value="delivery_ops">📦 Delivery Operations</option>
                  <option value="property_mgmt">🏗️ Property Management</option>
                  <option value="business_owner">💼 Business Owner</option>
                  <option value="valet">🤵 Valet / Parking Operator</option>
                  <option value="investor">📊 Investor / Advisor</option>
                  <option value="media">📰 Press / Media</option>
                  <option value="curious">👀 Just curious!</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-[10px] font-semibold text-[rgba(255,255,255,.6)] uppercase tracking-wider mb-2">
                  How did you discover TAGO?
                </label>
                <select
                  name="source"
                  value={formData.source}
                  onChange={handleInputChange}
                  className="w-full bg-[#040F1E] border border-[rgba(0,196,180,.35)] rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2300C4B4' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 14px center',
                    paddingRight: '40px'
                  }}
                >
                  <option value="">Select source</option>
                  <option value="instagram">Instagram</option>
                  <option value="facebook">Facebook</option>
                  <option value="tiktok">TikTok</option>
                  <option value="snapchat">Snapchat</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="whatsapp_group">WhatsApp Group</option>
                  <option value="friend_referral">Friend / Word of Mouth</option>
                  <option value="google">Google Search</option>
                  <option value="news_media">News / Media</option>
                  <option value="qr_scan">I scanned a TAGO tag!</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <button
                type="submit"
                className="w-full bg-[rgba(0,196,180,.08)]0 hover:bg-teal-600 text-white font-syne text-base sm:text-lg font-bold rounded-xl px-6 py-4 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-teal-500/30 group"
              >
                Reserve My Founding Spot <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
              </button>

              <p className="text-xs text-[rgba(255,255,255,.5)] text-center mt-4 leading-relaxed">
                By joining, you agree to our <a href="#" className="text-[#00C4B4] hover:underline">Privacy Policy</a>. No spam, ever.<br />
                <span className="font-cairo block mt-1 text-[rgba(255,255,255,.4)]" dir="rtl">
                  بالانضمام، أنت توافق على سياسة الخصوصية الخاصة بنا.
                </span>
              </p>
            </form>
          ) : (
            <div className="py-10 text-center">
              <div className="w-16 h-16 bg-teal-100 border-2 border-teal-500 rounded-full flex items-center justify-center text-3xl mx-auto mb-5 animate-bounce">
                ✓
              </div>
              <h2 className="font-syne text-2xl sm:text-3xl font-bold text-[#00C4B4] mb-3">You're on the list!</h2>
              <p className="text-sm sm:text-base text-[rgba(255,255,255,.6)] leading-relaxed mb-4">
                Welcome to TAGO's founding community.<br />
                We'll WhatsApp you the moment TAGO launches.<br /><br />
                <span className="font-cairo text-sm text-[rgba(255,255,255,.4)] block mt-2" dir="rtl">
                  أنت على قائمة المؤسسين. سنرسل لك رسالة واتساب فور الإطلاق.
                </span>
              </p>
              <span className="inline-block bg-teal-100 border border-[rgba(0,196,180,.14)] rounded-full px-5 py-2 text-sm font-semibold text-[#00C4B4]">
                You're #{waitlistCount} of our founding members
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Social Proof */}
      <div className="relative z-10 flex items-center justify-center gap-4 mb-8 px-4">
        <div className="flex -space-x-2">
          {['bg-orange-500', 'bg-purple-600', 'bg-[rgba(0,196,180,.08)]0', 'bg-amber-500'].map((color, i) => (
            <div key={i} className={`w-8 h-8 ${color} text-white rounded-full border-2 border-white flex items-center justify-center text-xs font-bold`}>
              {['A', 'M', 'F', 'S'][i]}
            </div>
          ))}
        </div>
        <p className="text-sm text-[rgba(255,255,255,.6)]">
          <strong className="text-white">{waitlistCount} founding members</strong> already registered
        </p>
      </div>

      {/* Footer */}
      <footer className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-[rgba(0,196,180,.14)]">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-8 mb-8">
          <div>
            <span className="font-syne text-3xl font-extrabold text-[#00C4B4] block mb-2">TAGO</span>
            <span className="text-base text-[rgba(255,255,255,.7)] max-w-xs block font-dm font-medium">
              Tag & Reach.
            </span>
          </div>

          <div>
            <div className="text-[10px] font-bold text-[rgba(255,255,255,.4)] uppercase tracking-widest mb-3">Company</div>
            <div className="flex gap-5 text-sm text-[rgba(255,255,255,.6)]">
              <a href="/privacy" className="hover:text-[#00C4B4] transition-colors">Privacy Policy</a>
              <a href="/terms" className="hover:text-[#00C4B4] transition-colors">Terms</a>
              <a href="mailto:tago.bots@gmail.com" className="hover:text-[#00C4B4] transition-colors">Contact</a>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold text-[rgba(255,255,255,.4)] uppercase tracking-widest mb-3">Follow Us</div>
            <div className="flex gap-2">
              {[
                { icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z', label: 'Instagram' },
                { icon: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z', label: 'Facebook' },
                { icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z', label: 'LinkedIn' },
              ].map((social, i) => (
                <a
                  key={i}
                  href="#"
                  aria-label={social.label}
                  className="w-10 h-10 rounded-xl bg-[#0C2040] border border-[rgba(0,196,180,.14)] hover:bg-[rgba(0,196,180,.08)] hover:border-[rgba(0,196,180,.35)] hover:-translate-y-0.5 flex items-center justify-center transition-all group"
                >
                  <svg className="w-4 h-4 fill-gray-600 group-hover:fill-teal-600 transition-colors" viewBox="0 0 24 24">
                    <path d={social.icon} />
                  </svg>
                </a>
              ))}
              <a
                href="#"
                aria-label="WhatsApp"
                className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 hover:-translate-y-0.5 flex items-center justify-center transition-all group"
              >
                <svg className="w-4 h-4 fill-emerald-600 transition-colors" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-[rgba(0,196,180,.14)]">
          <div className="flex flex-col sm:flex-row items-center gap-2">
            <span className="text-xs text-[rgba(255,255,255,.4)]">© 2026 TAGO · Kingdom of Bahrain · All rights reserved</span>
            <span className="hidden sm:inline text-[rgba(255,255,255,.2)]">·</span>
            <span className="inline-flex items-center gap-1.5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full px-2.5 py-0.5 text-[10px] font-bold text-[#FF6B35] uppercase tracking-wider">Beta</span>
          </div>
          <div className="flex gap-4">
            <a href="/privacy" className="text-xs text-[rgba(255,255,255,.4)] hover:text-[#00C4B4] transition-colors">Privacy Policy</a>
            <a href="/terms" className="text-xs text-[rgba(255,255,255,.4)] hover:text-[#00C4B4] transition-colors">Terms of Service</a>
          </div>
        </div>
      </footer>

      {/* Chat Bot Button */}
      <button
        onClick={() => setChatOpen(!chatOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 rounded-full shadow-xl hover:shadow-2xl hover:scale-110 transition-all flex items-center justify-center text-white"
        aria-label="Chat with TAGO AI"
      >
        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.96 9.96 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.95 7.95 0 01-4.079-1.116l-.293-.174-3.016.896.896-3.016-.174-.293A7.95 7.95 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8zm4.406-5.845c-.23-.115-1.36-.671-1.57-.748-.21-.077-.364-.115-.517.115-.153.23-.594.748-.728.902-.134.153-.267.172-.498.057-.23-.115-.97-.358-1.847-1.14-.683-.61-1.144-1.362-1.279-1.592-.134-.23-.014-.354.1-.469.103-.103.23-.267.345-.402.115-.134.153-.23.23-.384.077-.153.038-.287-.019-.402-.057-.115-.517-1.247-.708-1.707-.187-.448-.376-.387-.517-.394-.134-.006-.287-.008-.44-.008-.153 0-.402.057-.613.287-.21.23-.804.786-.804 1.918 0 1.132.824 2.225.939 2.379.115.153 1.621 2.476 3.927 3.472.549.237.977.378 1.31.484.55.175 1.051.15 1.447.092.441-.066 1.36-.556 1.552-1.093.19-.537.19-.997.133-1.093-.057-.096-.21-.153-.44-.268z"/>
        </svg>
      </button>

      {/* Chat Window */}
      {chatOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-full max-w-sm bg-[#0C2040] border border-[rgba(0,196,180,.14)] rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-teal-600 to-teal-500 p-4 flex items-center justify-between">
            <div>
              <div className="font-syne text-sm font-bold text-white">TAGO AI Assistant</div>
              <div className="text-[10px] text-white/70">⚡ Instant answers about TAGO</div>
            </div>
            <button
              onClick={() => setChatOpen(false)}
              className="text-white/70 hover:text-white text-xl leading-none transition-colors"
            >
              ✕
            </button>
          </div>

          <div className="h-80 overflow-y-auto p-4 bg-[#080e18] space-y-2">
            {chatMessages.map((msg, i) => (
              <div
                key={i}
                className={`max-w-[88%] rounded-xl p-3 text-sm leading-relaxed ${
                  msg.type === 'bot'
                    ? 'bg-[#1f3340] border-l-2 border-teal-500 rounded-tl-sm text-white/90'
                    : 'bg-[#025144] rounded-tr-sm text-white/90 ml-auto'
                }`}
                dangerouslySetInnerHTML={{ __html: msg.text }}
              />
            ))}
          </div>

          {showQuickReplies && (
            <div className="flex flex-wrap gap-2 p-4 bg-[#0C2040] border-t border-[rgba(0,196,180,.14)]">
              {['How does it work?', 'When does it launch?', 'Is it free?', 'Business pricing?'].map((reply) => (
                <button
                  key={reply}
                  onClick={() => sendQuickReply(reply)}
                  className="bg-teal-100 hover:bg-teal-200 border border-[rgba(0,196,180,.35)] rounded-full px-3 py-1.5 text-xs font-medium text-teal-700 transition-colors"
                >
                  {reply}
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-2 p-3 border-t border-[rgba(0,196,180,.14)] bg-[#0C2040]">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendChatMessage(chatInput)}
              placeholder="Ask anything about TAGO…"
              className="flex-1 bg-[#040F1E] border border-[rgba(0,196,180,.35)] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <button
              onClick={() => sendChatMessage(chatInput)}
              className="w-9 h-9 bg-[rgba(0,196,180,.08)]0 hover:bg-teal-600 rounded-xl flex items-center justify-center text-white transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagoLanding;
